import { NextRequest, NextResponse } from 'next/server';
import Crypto from '@/utils/crypto';
import { getEnvironmentDetails } from '@/utils/env';
import ICookies from './Cookie.types';

/**
 * see the {@link node_modules/next/dist/compiled/@edge-runtime/cookies/index.d.ts}
 */
interface ICookieSerializeOptions {
  domain: string | undefined;
  expires: Date | undefined;
  httpOnly: boolean | undefined;
  maxAge: number | undefined;
  path: string | undefined;
  priority: 'low' | 'medium' | 'high' | undefined;
  sameSite: true | false | 'lax' | 'strict' | 'none' | undefined;
  secure: boolean | undefined;
}

type TCookieName = keyof ICookies;
type TCookieValue<K extends TCookieName> = ICookies[K] | null;

export interface IRequestResponse {
  request: NextRequest;
  response: NextResponse;
}

export type TCookieOptionsV2 = Partial<ICookieSerializeOptions> & {
  rawName?: boolean;
  shouldUseCompression?: boolean;
};

const { isDevelopmentEnvironment } = getEnvironmentDetails();

const defaultCookieOptions: TCookieOptionsV2 = {
  httpOnly: true,
  sameSite: isDevelopmentEnvironment ? 'lax' : 'none',
  secure: isDevelopmentEnvironment ? false : true,
  maxAge: undefined,
  path: '/',
};

//-----------------------------------------------------------
const { encrypt, decrypt, compressAndEncrypt, decompressAndDecrypt } = Crypto<
  TCookieValue<any>
>(`${process.env.SSR_COOKIE_SECRET}`);
const getCookieName = (
  cookieName: string,
  options?: Pick<TCookieOptionsV2, 'rawName'>
): string => {
  if (options?.rawName) {
    delete options.rawName;
    return cookieName;
  }

  const encoded: string = `${Math.abs(
    cookieName.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0)
  )}`;

  return encoded;
};

export function setCookie<K extends TCookieName>(
  response: NextResponse,
  cookieName: K,
  rawValue: NonNullable<TCookieValue<K>>,
  options?: TCookieOptionsV2
): void {
  const cookieKey = getCookieName(cookieName, options);

  // Determine the cookie value, optionally compressing and encrypting
  const cookieValue = options?.shouldUseCompression
    ? compressAndEncrypt(rawValue)
    : encrypt(rawValue);

  // Merge provided options with the default settings
  const cookieOptions = { ...defaultCookieOptions, ...options };

  // Set the cookie on the response
  response.cookies.set({
    ...cookieOptions,
    name: cookieKey,
    value: cookieValue,
  });
}

export function getCookieValue<K extends TCookieName>(
  { request, response }: IRequestResponse,
  cookieName: K,
  options?: Pick<TCookieOptionsV2, 'rawName' | 'shouldUseCompression'>
): TCookieValue<K> {
  try {
    const cookieKey: string = getCookieName(cookieName, options);

    const cookie =
      response.cookies.get(cookieKey) || request.cookies.get(cookieKey);

    if (!cookie?.value) {
      return null;
    }

    const value = options?.shouldUseCompression
      ? decompressAndDecrypt(cookie.value)
      : decrypt(cookie.value);

    return value;
  } catch (e) {
    return null; // Explicitly return null in case of any error
  }
}

export function deleteCookie(
  response: NextResponse,
  cookieName: TCookieName,
  options?: TCookieOptionsV2
): void {
  const cookieKey = getCookieName(cookieName, options);

  // Merge provided options with default settings
  const cookieOptions = { ...defaultCookieOptions, ...options };

  // Delete the cookie from the response
  response.cookies.delete({
    ...cookieOptions,
    name: cookieKey,
  });
}
